<template>
  <v-dialog v-model="active" width="800" persistent>
    <template v-slot:activator="{ on }">
      <v-btn v-on="on" outlined style="margin-right:1em">
        <v-icon left>fal fa-file-excel</v-icon>
        Export
      </v-btn>
    </template>
    <v-card>
      <v-card-title>Export</v-card-title>
      <v-card-text>
        <v-row>
          <v-col :cols="12" md="6">
            <v-card elevation="2">
              <v-toolbar elevation="1">
                <v-toolbar-title>Available Fields</v-toolbar-title>
              </v-toolbar>
              <v-treeview v-model="exportFields" :items="exportOptions" selectable return-object style="max-height:450px;overflow:auto"></v-treeview>
            </v-card>
          </v-col>
          <v-col :cols="12" md="6">
            <v-card elevation="2">
              <v-toolbar elevation="1">
                <v-toolbar-title>Selected Fields (Drag to reorder)</v-toolbar-title>
              </v-toolbar>
              <v-list dense style="max-height:450px;overflow:auto">
                <draggable v-model="exportFields">
                  <v-list-item v-for="({ name, value }, index) in exportFields" :key="'item-' + index">
                    <v-list-item-title>{{ name }}</v-list-item-title>
                    <v-list-item-action style="margin:4px 0">
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-btn v-on="on" icon color="red" @click="exportFields.splice(index, 1)" small>
                            <v-icon small>fal fa-times-circle</v-icon>
                          </v-btn>
                        </template>
                        <span>Remove this field</span>
                      </v-tooltip>
                    </v-list-item-action>
                  </v-list-item>
                </draggable>
              </v-list>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-btn text @click="active = false">Cancel</v-btn>
        <v-btn text color="success" :disabled="exportRunning || exportFields.length === 0" @click="runExport">{{ exportRunning ? 'Running...' : 'Export' }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { ref } from '@vue/composition-api'
import { saveAs } from 'file-saver'

export default {
  components: {
    Draggable: () => import('vuedraggable')
  },
  setup (props, { root }) {
    const active = ref(false)
    const exportRunning = ref(false)
    const exportFields = ref([
      { name: 'Banner Id', value: 'bannerId', id: 1 },
      { name: 'Last Name', value: 'last', id: 2 },
      { name: 'Preferred Name', value: 'preferred', id: 3 },
      { name: 'Covenant Email', value: 'email', id: 4 },
      { name: 'Home Email', value: 'homeEmail', id: 5 },
      { name: 'Sections Completed', value: 'done.sections', id: 21 },
      { name: 'Section Completion Percent', value: 'done.sectionPercent', id: 22 }
    ])
    const exportOptions = ref([
      { name: 'Banner Id', value: 'bannerId', id: 1 },
      { name: 'Last Name', value: 'last', id: 2 },
      { name: 'Preferred Name', value: 'preferred', id: 3 },
      { name: 'Covenant Email', value: 'email', id: 4 },
      { name: 'Home Email', value: 'homeEmail', id: 5 },
      {
        name: 'Academics',
        id: 10,
        children: [
          { name: 'Major(s)', value: 'academics.major', id: 11 },
          { name: 'Minor(s)', value: 'academics.minor', id: 12 },
          { name: 'Concentration(s)', value: 'academics.conc', id: 13 },
          { name: 'Pre-Registered', value: 'academics.preReg', id: 14 }
        ]
      },
      {
        name: 'Completion',
        id: 20,
        children: [
          { name: 'Sections Completed', value: 'done.sections', id: 21 },
          { name: 'Section Completion Percent', value: 'done.sectionPercent', id: 22 },
          { name: 'Slides Completed', value: 'done.slides', id: 23 },
          { name: 'Slide Completion Percent', value: 'done.slidePercent', id: 24 }
        ]
      },
      {
        name: 'Sections',
        id: 30,
        children: [
          { name: 'Last Section Completed', value: 'sections.last', id: 31 },
          { name: 'All Section Statuses', value: 'sections.status', id: 32 }
        ]
      },
      {
        name: 'To-Do List',
        id: 50,
        children: [
          {
            name: 'Placement Tests',
            id: 51,
            children: [
              { name: 'Math Placement 1 - Basic Algebra', value: 'actions.title=Math Placement Test Part 1 - Basic Algebra', id: 52 },
              { name: 'Math Placement 2 - Advanced Algebra', value: 'actions.title=Math Placement Test Part 2 - Advanced Algebra', id: 53 },
              { name: 'Math Placement 3 - Trigonometry', value: 'actions.title=Math Placement Test Part 3 - Trigonometry', id: 54 },
              { name: 'Foreign Language Test - French/German/Spanish', value: 'actions.title=French/German/Spanish Placement Test', id: 55 },
              { name: 'Foreign Language Test - Latin', value: 'actions.title=Latin Placement Test', id: 56 },
              { name: 'Music Theory Placement Test', value: 'actions.title=Music Theory Placement Test', id: 57 },
              { name: 'Writing Test', value: 'actions.title=Accuplacer Next Generation Writing Test', id: 58 }
            ]
          },
          { name: 'Pre-Register for Classes', value: 'actions.title=Pre-Register for Classes', id: 60 }
        ]
      }
    ])

    async function runExport () {
      const header = []
      for (const { name, value } of exportFields.value) {
        if (value === 'sections.status') {
          let sections = root.$store.state.admin.sections
          if (sections.length === 0) {
            await root.$store.dispatch('admin/loadSections', root.$store.state.admin.term)
            sections = root.$store.state.admin.sections
          }
          for (const { code, label } of sections) {
            header.push({ header: label, align: 'left', key: code })
          }
        } else if (value === 'done.slidePercent' || value === 'done.sectionPercent') {
          header.push({ header: name, align: 'right', key: value, numFmt: '0.00%' })
        } else {
          header.push({ header: name, align: 'left', key: value })
        }
      }
      const rows = []
      let dt = new Date()
      const filename = 'Scots Connect Export ' + dt.toISOString().substr(0, 10) + '.xlsx'
      const service = root.$feathers.service('orientation/tracker')
      exportRunning.value = true
      // Load the current list filters
      const query = await root.$store.dispatch('admin/buildStudentQuery')
      query.$limit = 0
      // Load the number of students
      const { total } = await service.find({ query })
      query.$limit = 20
      // Load the list, one page at a time
      for (let skip = 0; skip < total; skip += query.$limit) {
        query.$skip = skip
        const { data } = await service.find({ query })
        for (let i = 0; i < data.length; i++) {
          // Add the needed data to the export array
          let row = {}
          for (const { value } of exportFields.value) {
            if (value.indexOf('=') > 0) {
              const [field, name] = value.split('=')
              if (field.indexOf('.') > 0) {
                const [first, second] = field.split('.')
                if (Array.isArray(data[i][first])) {
                  for (let j = 0; j < data[i][first].length; j++) {
                    if (data[i][first][j][second] === name) {
                      if (first === 'sections') {
                        row[value] = data[i][first][j].status
                      } else if (first === 'blocks') {
                        row[value] = data[i][first][j].complete ? 'Y' : 'N'
                      } else if (first === 'actions') {
                        const { done, dismissed, clicked, viewed } = data[i][first][j]
                        if (done) row[value] = 'Done'
                        else if (dismissed) row[value] = 'Dismissed'
                        else if (clicked) row[value] = 'Clicked'
                        else if (viewed) row[value] = 'Viewed'
                        else row[value] = 'Added'
                      }
                      break
                    }
                  }
                }
              }
            } else if (value.indexOf('.') > 0) {
              const [first, second] = value.split('.')
              if (first === 'sections') {
                if (second === 'last') {
                  if (data[i].sections.length > 0) {
                    row[value] = data[i].sections[data[i].sections.length - 1].section
                  }
                } else if (second === 'status') {
                  for (let j = 0; j < data[i].sections.length; j++) {
                    row[data[i].sections[j].section] = data[i].sections[j].status
                  }
                } else {
                  for (let j = 0; j < data[i].sections.length; j++) {
                    if (data[i].sections[j].section === second) {
                      row[value] = data[i].sections[j].status
                      break
                    }
                  }
                }
              } else if (first === 'done') {
                row[value] = 0
                if (second === 'sections' || second === 'sectionPercent') {
                  for (let j = 0; j < data[i].sections.length; j++) {
                    if (data[i].sections[j].status === 'Complete') row[value]++
                  }
                  if (second === 'sectionPercent') row[value] /= root.$store.state.admin.sectionCount
                } else if (second === 'slides' || second === 'slidePercent') {
                  for (let j = 0; j < data[i].sections.length; j++) {
                    if (data[i].sections[j].status === 'Complete') row[value] += data[i].sections[j].highSlide
                  }
                  if (second === 'slidePercent') row[value] /= root.$store.state.admin.slideCount
                }
              } else {
                row[value] = data[i][first][second]
              }
            } else {
              row[value] = data[i][value]
            }
            if (Array.isArray(row[value])) row[value] = row[value].join('; ')
          }
          rows.push(row)
        }
      }
      // Send the export to the server to build the file
      let data = await root.$feathers.service('export/xlsx').create({ filename, header, rows })
      // Download the exported file
      saveAs(new Blob([data.buffer]), filename)
      // Close the export window
      exportRunning.value = false
      active.value = false
    }

    return {
      active,
      exportRunning,
      exportFields,
      exportOptions,
      runExport
    }
  }
}
</script>
